<template>
  <div class="container-fluid">
    <div class="full-height-page-wrapper">

    <PageHeader showBreadcrumbs page-title="Expenses">
      <template #buttons>
        <b-button :variant="filterShow ? 'secondary' : 'info'" @click="toggleFilter"
          class="button title-container__controls-item">
          <span class="material-icons icon-image-preview">
            tune
          </span>
          Filters
        </b-button>
        <b-button :variant="isShowBulkCreation ? 'secondary' : 'info'" @click="isShowBulkCreation = !isShowBulkCreation"
          class="button title-container__controls-item">
          <span class="material-icons icon-image-preview">
            tune
          </span>
          Bulk Creation
        </b-button>
        <b-button @click.prevent="editEntityModalOpen" class="button" type="button"
          v-if="$can('create-finances_expenses')" variant="success">
          Create
        </b-button>
      </template>
    </PageHeader>

    <Filters v-if="$can('read_all-finances_expenses')" ref="filtersComponent" v-show="filterShow" resetToInitial :data="filters"
      :isLoading="loading && isInitialDataLoaded" @submit="getEntitiesByFilter" textButtonSubmit="Filter" />

    <ExpensesBulkCreation @reloadExpenses="getEntities" v-show="isShowBulkCreation" />

    <div v-if="loading && !isInitialDataLoaded" class="content-loading-wrapper">
      <Preloader class="centered-preloader" />
    </div>

    <div v-else class="full-height-content">

    <FinancesExpensesForm :bankCards="bankCards" :currencies="currencies" :dataProps="lastExpensePreFillData"
      :edit="false" :expensesCategories="expensesCategories" :expensesStatuses="expensesStatuses" :isLoading="isModalLoading"
      :modal="true" :ownBankDetails="ownBankDetails" :projects="getProjects" :show-preloader="true" :users="getAllUsers"
      @submit="create" v-if="showForm" v-model="showForm" />

    <div v-if="table.data.length" class="col-12">
      <div class="table-wrapper">
        <TableForPage ref="table" :isLoading="loading" :isDataLoading="loading && isInitialDataLoaded" :tableData="table.data"
          :fields="table.fields" :slots="table.slots" :isEdit="$can('update_own-finances_expenses')"
          :isRemove="$can('delete_own-finances_expenses')">
          <template v-slot:specialist="data">
            <div class="specialist-user-name">
              {{ data.item.user.name }}
            </div>
            <div class="specialist-user-position">
              {{ data.item.user.position }}
            </div>
          </template>
          <template v-slot:expenses_category.display_name="data">
            <div>
              <span v-if="data.item.related_project" v-b-tooltip.html
                :title="'Related to project ' + data.item.related_project.name">
                <font-awesome-icon icon="link" />
              </span>
              {{ data.item.expenses_category.display_name }}
            </div>
          </template>
          <template v-slot:bank_card="data">
            <LabelBankCard :card_details="getCardDetail(data.item)" />
          </template>
          <template v-slot:amount="data">
            <span>
              {{ data.item.amount ? `${data.item.amount} ${data.item.currency.code}` : '' }}
            </span>
          </template>
          <template v-slot:due_date="data">
            <span>
              {{ data.item.due_date }} / <template
                v-if="data.item.paid_date">{{data.item.paid_date}}</template><template v-else>&ndash;</template>
            </span>
          </template>
          <template v-slot:expenses_status="data">
            <LabelTableStatus :status="data.item.expenses_status" />
          </template>
          <template v-slot:links="data">
            <ExpensesLinks :item="data.item" @reloadExpenses="getEntities" />
          </template>
        </TableForPage>

        <div class="mt-3">
          <b-card-group deck class="mb-3">
            <MoneyTotalsCard title="Original totals" :data="table.totalsData.byAllExpenses.original" />
            <MoneyTotalsCard title="Equivalent totals" :data="table.totalsData.byAllExpenses.equivalent" />
          </b-card-group>
        </div>

        <error-modal ref="errorModal" />
      </div>
    </div>

    <EmptyStateNoResults
      v-else-if="isInitialDataLoaded && !table.data.length && !table.isAnyData"
      customIcon="folder_off"
      customIconClasses="material-symbols-outlined empty-state__icon"
      title="No expenses added"
      description="You haven't created any expenses yet. Start by adding one to manage your spendings."
      buttonText="CREATE"
      @button-click="handleCreateClick"
    />
    <EmptyStateNoResults
      v-else-if="isInitialDataLoaded && !table.data.length && table.isAnyData"
      @reset-filters="handleResetFilters"
    />
    <div v-else-if="isInitialDataLoaded && !loading" class="col-12">
      <div class="table-wrapper">Use filters to get expenses list</div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import Moment from 'moment'
import { getDateFrom } from '../functions/format_date'
import ExpensesLinks from '../components/ExpensesLinks'
import FinancesExpensesForm from '../components/forms/FinancesExpensesForm'
import FinancesMixin from '../mixins/finances'
import ExpenseForm from '../mixins/ExpenseForm'
import ErrorModal from '../components/modals/ErrorModal.vue'
import Filters from '@components/Filter/Filter'
import TableForPage from '@tables/TableForPage'
import LabelTableStatus from '@components/tables/elements/LabelTableStatus'
import LabelBankCard from '@components/tables/elements/LabelBankCard'
import ExpensesBulkCreation from '../components/ExpensesBulkCreation'
import MoneyTotalsCard from '@components/MoneyTotalsCard'
import PageHeader from '@components/PageHeader.vue'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  name: 'FinancesExpenses',
  components: {
    PageHeader,
    ExpensesLinks,
    ErrorModal,
    FinancesExpensesForm,
    Filters,
    TableForPage,
    LabelTableStatus,
    LabelBankCard,
    ExpensesBulkCreation,
    MoneyTotalsCard,
    Preloader,
    EmptyStateNoResults
  },
  mixins: [FinancesMixin, ExpenseForm],
  data () {
    return {
      filterShow: true,
      loading: true,
      isModalLoading: false,
      firstLoad: false,
      isAllOptionsFilterSet: false,
      isExpensesDataLoaded: false,
      showForm: false,
      defaultFormData: {},
      editedItem: {},
      selectedFilters: {},
      lastExpensePreFillData: {},
      isShowBulkCreation: false,
      filters: {
        'date_from': {
          type: 'datepicker',
          name: 'date_from',
          label: 'Date from',
          format: 'dd/MM/yyyy',
          value: Moment().startOf('month').format('MM/DD/YYYY'),
          defaultValue: Moment().startOf('month').format('MM/DD/YYYY'),
          class: 'col-md-2'
        },
        'date_to': {
          type: 'datepicker',
          name: 'date_to',
          label: 'Date to',
          format: 'dd/MM/yyyy',
          value: Moment().endOf('month').format('MM/DD/YYYY'),
          defaultValue: Moment().endOf('month').format('MM/DD/YYYY'),
          class: 'col-md-2'
        },
        'users': {
          type: 'multiselect',
          name: 'user_id',
          label: 'Select users',
          valueField: 'id',
          selectLabel: 'label',
          options: [],
          class: 'col-md-2'
        },
        'category': {
          type: 'multiselect',
          name: 'expenses_category_id',
          label: 'Select category',
          valueField: 'id',
          selectLabel: 'display_name',
          options: [],
          class: 'col-md-2'
        },
        'status': {
          type: 'multiselect',
          name: 'expenses_status_id',
          label: 'Select status',
          valueField: 'id',
          selectLabel: 'display_name',
          options: [],
          class: 'col-md-2'
        },
        'project_related': {
          type: 'multiselect',
          name: 'project_related',
          label: 'Select type',
          valueField: 'type',
          selectLabel: 'label',
          options: [{ type: 'regular', label: 'Regular' }, { type: 'project_related', label: 'Project related' }],
          value: [],
          class: 'col-md-2'
        }
      },
      table: {
        slots: {
          'specialist': {
            name: 'specialist'
          },
          'expenses_category.display_name': {
            name: 'expenses_category.display_name'
          },
          bank_card: {
            name: 'bank_card'
          },
          amount: {
            name: 'amount'
          },
          due_date: {
            name: 'due_date'
          },
          expenses_status: {
            name: 'expenses_status'
          },
          links: {
            name: 'links'
          }
        },
        fields: [
          {
            key: 'specialist',
            label: 'specialist',
            sortable: true
          },
          {
            key: 'expenses_category.display_name',
            label: 'Category',
            sortable: true
          },
          {
            key: 'amount',
            label: 'Amount',
            sortable: true,
            class: 'column-bd-name'
          },
          {
            key: 'expenses_status',
            label: 'Status',
            sortable: true,
            class: 'column-icon text-center'
          },
          {
            key: 'bank_card',
            label: 'Bank card (Account)',
            sortable: false,
            class: 'column-bank-card text-center'
          },
          {
            key: 'comment',
            label: 'Comment'
          },
          {
            key: 'due_date',
            label: 'Due at / Paid at',
            sortable: true
          },
          {
            key: 'links',
            label: 'Actions',
            class: 'text-center'
          }
        ],
        data: [],
        totalsData: []
      }
    }
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isExpensesDataLoaded
    }
  },
  created () {
    this.fetchAllData().then(() => {
      this.getEntitiesByFilter(this.filters)
    })

    if (this.$can('read_own-finances_expenses') && !this.$can('read_all-finances_expenses')) {
      this.filter.users.push(this._UserInfo)
    }
    this.fetchFormLists().then(() => {
      // this.getLastExpensePreFillData() // alternative for loading pre fill data on each modal form open
    })
  },
  watch: {
    'filter.date_from': function (val) {
      if (Moment(val, 'YYYY-MM-DD').isAfter(this.filter.date_to, 'YYYY-MM-DD')) {
        this.filter.date_to = this.filter.date_from
      }
    }
  },
  methods: {
    getCardDetail (item) {
      let result = ''

      if (item.bank_card) {
        result = item.bank_card.name
      }

      if (item.bank_detail) {
        result += `\n ${item.bank_detail.summary}`
      }

      return result
    },
    async fetchAllUsers () {
      return this.$api.users.getAllUsers()
    },
    async fetchExpensesStatuses () {
      return this.$api.expenses.getExpensesStatuses()
    },
    async fetchExpensesCategories () {
      return this.$api.expenses.getExpensesCategories()
    },
    setAllOptionsFilter () {
      this.filters.status.options = this.expensesStatuses
      this.filters.category.options = this.expensesCategories
      this.filters.users.options = this.allUsers

      this.isAllOptionsFilterSet = true
    },
    fetchAllData () {
      return Promise.all([
        this.fetchAllUsers(),
        this.fetchExpensesStatuses(),
        this.fetchExpensesCategories()
      ])
        .then(() => {
          this.setAllOptionsFilter()
        })
    },
    toggleFilter () {
      this.filterShow = !this.filterShow
    },
    resetFilter (callback) {
      this.filter.users = []
      this.filter.expensesCategories = []
      this.filter.date_from = Moment().startOf('month').format('MM/DD/YYYY')
      this.filter.date_to = Moment().endOf('month').format('MM/DD/YYYY')

      if (callback) {
        callback()
      }
    },
    getDate (date, target) {
      let result = getDateFrom(date, target)
      if (target === 'from') {
        this.filter.date_from = result
      } else {
        this.filter.date_to = result
      }
    },
    prepareDataForGetReports (fields) {
      let data = {}

      for (let key in fields) {
        const name = fields[key]['name']
        let value = fields[key]['value']

        if (Object.prototype.toString.call(value) === '[object Date]') {
          value = this._toLocalTime(value)
        }

        if (Array.isArray(value) && value.length && fields[key]['valueField']) {
          value = value.map(item => item[fields[key]['valueField']])
        }

        data[name] = value
      }

      return data
    },
    resetPagination () {
      this.$refs['table'] && this.$refs['table'].resetPagination()
    },
    async getEntitiesByFilter (fields) {
      this.selectedFilters = fields
      await this.getEntities()
      this.resetPagination()
    },
    getEntities () {
      const params = this.prepareDataForGetReports(this.selectedFilters)
      this.loading = true
      return this.$api.expenses.getExpenses(params)
        .then(response => {
          this.table.data = response.data.collection
          this.table.totalsData = response.data.total
          this.table.isAnyData = response.data.isAnyData
          this.parentCategories = this.table.data.filter(cat => cat.parent_id === null || cat.parent_id === undefined)
          this.parentCategories.unshift({ id: '', 'name': 'No parent category' })
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.loading = false
          this.firstLoad = true
          this.isExpensesDataLoaded = true
        })
    },
    async getLastExpensePreFillData () {
      return this.$api.expenses.getLastExpensePreFillData()
        .then(response => {
          const expensesCategory = this.expensesCategories.find(
            (item) => item.id === response.data.expenses_category_id
          )
          const bankCard = this.bankCards.find(
            (item) => item.id === response.data.bank_card_id
          )
          const bankDetail = this.ownBankDetails.find(
            (item) => item.id === response.data.bank_detail_id
          )
          this.lastExpensePreFillData = {
            expenses_category: expensesCategory,
            expenses_category_id: response.data.expenses_category_id,
            due_date: response.data.due_date,
            paid_date: response.data.paid_date,
            bank_card: bankCard,
            bank_card_id: response.data.bank_card_id,
            bank_detail: bankDetail,
            bank_detail_id: response.data.bank_detail_id,
            currency_id: response.data.currency_id,
            user_id: response.data.user_id
          }
        })
        .catch(error => {
          this.errorNotifications(error)
        })
    },
    create (data, files) {
      this.isModalLoading = true
      const params = this.prepareDataForEditAndCreateRequest(data)
      this.$api.expenses.createExpenses(params)
        .then(({ data }) => {
          this.$snotify.success(
            'Category successfully created',
            'Success',
            this.$store.state.snotify
          )

          this.showForm = false
          this.editedItem = {}
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isModalLoading = false
          this.getEntities()
        })
    },
    editEntityModalOpen (data = {}) {
      this.editedItem = Object.assign({}, data)
      this.showForm = true
      this.isModalLoading = true
      this.getLastExpensePreFillData()
        .finally(() => {
          this.isModalLoading = false
        })
    },
    handleResetFilters () {
      if (this.$refs.filtersComponent) {
        this.$refs.filtersComponent.resetFilter()
      }
    },
    handleCreateClick () {
      this.editEntityModalOpen()
    }
  }
}
</script>

<style scoped>
  .table-wrapper {
    padding-top: 20px;
  }
  .table-wrapper h3 {
    margin-bottom: 30px;
  }
  .spinner-border {
    border-width: 0.25rem !important;
  }
</style>
